// ==============================|| REACT & THIRD-PARTY IMPORTS ||============================== //
import React, { useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Divider,
    Typography,
    Stack,
    InputLabel,
    IconButton,
    Box,
    CircularProgress
} from '@mui/material';
import { CloseOutlined, RightOutlined, SaveOutlined } from '@ant-design/icons';
import { FormikProvider, useFormik, Form } from 'formik';
import * as Yup from 'yup';
import { motion } from 'framer-motion';

// ==============================|| TYPES IMPORTS ||============================== //
import { RenameFileFormProps, RenameFileFormValues } from 'types/CommonFileManager/commonFileManager';

// ==============================|| CONTEXT & HOOKS ||============================== //
import { useFileManagerState } from 'hooks/useFileManager';
import { getPathForDirectory } from './CommonFileManager';


export default function RenameFileForm({
    open: propOpen,
    onClose: propOnClose,
    onSubmit: propOnSubmit,
    fileId: propFileId,
    fileName: propFileName,
    parentPath: propParentPath
}: RenameFileFormProps) {
    const state = useFileManagerState();

    const open = propOpen ?? state.isRenameFileOpen;
    const onClose = propOnClose ?? (() => {
        state.setIsRenameFileOpen(false);
        state.setRenameFileItem(null);
    });
    const onSubmit = propOnSubmit ?? state.handleRenameFileSubmit;
    const fileId = propFileId ?? state.renameFileItem?.id ?? '';
    const fileName = propFileName ?? state.renameFileItem?.name ?? '';
    const parentPath = propParentPath ?? getPathForDirectory(state.directoryMap, state.selectedDirectoryId);

    const RenameFileSchema = Yup.object().shape({
        newFileName: Yup.string()
            .trim()
            .required('File name is required')
            .max(100, 'Name must be at most 100 characters')
            .test('no-invalid-chars', 'File name cannot contain < > : " / \\ | ? *', (value) => {
                if (!value) return true;
                return !/[<>:"/\\|?*]/.test(value);
            })
            .test('no-duplicate-name', 'File name already exists in this directory.', function (value) {
                if (!value || isBulk) return true;

                // Resolve final name with original extension if omitted
                let finalName = value.trim();
                const lastDotOriginal = fileName.lastIndexOf('.');
                if (lastDotOriginal !== -1) {
                    const originalExt = fileName.substring(lastDotOriginal);
                    const lastDotNew = finalName.lastIndexOf('.');
                    if (lastDotNew === -1) {
                        finalName = finalName + originalExt;
                    } else if (lastDotNew === finalName.length - 1) {
                        finalName = finalName + (originalExt.startsWith('.') ? originalExt.substring(1) : originalExt);
                    }
                }

                if (finalName.toLowerCase() === fileName.toLowerCase()) return true;

                const parentId = state.selectedDirectoryId || 'files';
                if (state.checkDuplicateName) {
                    return !state.checkDuplicateName(parentId, finalName, fileId);
                }
                return true;
            })
    });

    const isBulk = fileId.includes(',');
    const filesCount = isBulk ? fileId.split(',').length : 1;

    const formik = useFormik<RenameFileFormValues>({
        initialValues: {
            newFileName: isBulk ? '' : fileName
        },
        enableReinitialize: true,
        validateOnBlur: false,
        validationSchema: RenameFileSchema,
        onSubmit: async (values, { setSubmitting, resetForm }) => {
            try {
                let finalName = values.newFileName.trim();
                if (!isBulk) {
                    const lastDotOriginal = fileName.lastIndexOf('.');
                    if (lastDotOriginal !== -1) {
                        const originalExt = fileName.substring(lastDotOriginal);
                        const lastDotNew = finalName.lastIndexOf('.');
                        if (lastDotNew === -1) {
                            finalName = finalName + originalExt;
                        } else if (lastDotNew === finalName.length - 1) {
                            finalName = finalName + (originalExt.startsWith('.') ? originalExt.substring(1) : originalExt);
                        }
                    }
                }
                await onSubmit(fileId, finalName);
                resetForm();
                onClose();
            } catch (err) {
                console.error(err);
            } finally {
                setSubmitting(false);
            }
        }
    });

    useEffect(() => {
        formik.resetForm();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, fileName]);

    const handleClose = () => {
        if (formik.isSubmitting) return;
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={(event, reason) => {
                if (formik.isSubmitting) return;
                if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
                    onClose();
                }
            }}
            fullWidth
            maxWidth="sm"
            PaperProps={{
                sx: {
                    boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
                    overflow: 'hidden'
                }
            }}
            sx={{
                zIndex: 20000
            }}
        >
            <FormikProvider value={formik}>
                <Form noValidate onSubmit={formik.handleSubmit}>
                    <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="h6" component="div">
                            {isBulk ? (
                                <>
                                    <Typography component="span" sx={{ color: 'primary.main' }}>
                                        Rename
                                    </Typography>
                                    ({filesCount} Files)
                                </>

                            ) : (
                                <>
                                    <Typography component="span" sx={{ color: 'primary.main' }}>
                                        Rename
                                    </Typography>{' '}
                                    File
                                </>
                            )}
                        </Typography>
                        <IconButton
                            type="button"
                            aria-label="close"
                            onClick={handleClose}
                            disabled={formik.isSubmitting}
                            sx={{
                                color: (theme) => theme.palette.primary.main
                            }}
                        >
                            <CloseOutlined />
                        </IconButton>
                    </DialogTitle>
                    <Divider />

                    <DialogContent sx={{ p: 2.5 }}>
                        <Stack sx={{ gap: 2 }}>
                            {/* Folder Breadcrumbs */}
                            {parentPath && parentPath.length > 0 && (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', fontSize: '14px', fontWeight: 500, color: 'text.primary', gap: 0.5 }}>
                                    {parentPath.map((pathName, index) => {
                                        const isLast = index === parentPath.length - 1;
                                        return (
                                            <React.Fragment key={index}>
                                                {index > 0 && <Box component="span" sx={{ color: 'text.primary', fontWeight: 500, mx: 0.2 }}>

                                                    <RightOutlined style={{ color: 'text.primary', fontSize: '13px', fontWeight: 600 }} />

                                                </Box>}
                                                <Box
                                                    title={pathName}
                                                    component="span"
                                                    sx={{
                                                        color: isLast ? 'primary.main' : 'text.primary',
                                                        fontWeight: isLast ? 500 : 400,
                                                        whiteSpace: 'nowrap',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        maxWidth: '150px',
                                                        display: 'inline-block',
                                                        verticalAlign: 'bottom'
                                                    }}
                                                >
                                                    {pathName}
                                                </Box>
                                            </React.Fragment>
                                        );
                                    })}
                                </Box>
                            )}
                            <Stack sx={{ gap: 1 }}>
                                <InputLabel htmlFor="rename-file-name" required>
                                    {isBulk ? 'New Files Name' : 'File Name'}
                                </InputLabel>
                                <TextField
                                    autoFocus
                                    fullWidth
                                    id="rename-file-name"
                                    placeholder={isBulk ? "Enter New Files Name" : "Enter File Name"}
                                    {...formik.getFieldProps('newFileName')}
                                    error={Boolean(formik.touched.newFileName && formik.errors.newFileName)}
                                    helperText={formik.touched.newFileName && formik.errors.newFileName}
                                    slotProps={{ htmlInput: { maxLength: 100 } }}
                                    disabled={formik.isSubmitting}
                                />
                            </Stack>
                        </Stack>
                    </DialogContent>
                    <Divider />

                    <DialogActions sx={{ p: 2 }}>
                        <Stack direction="row" sx={{ gap: 2, alignItems: 'center', width: 1, justifyContent: 'flex-end' }}>
                            <Button
                                type="submit"
                                variant="contained"
                                color="primary"
                                disabled={formik.isSubmitting || !formik.dirty}
                                startIcon={
                                    formik.isSubmitting ? (
                                        <motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}>
                                            <CircularProgress size={16} color="inherit" />
                                        </motion.div>
                                    ) : (
                                        <SaveOutlined />
                                    )
                                }
                            >
                                Update
                            </Button>
                        </Stack>
                    </DialogActions>
                </Form>
            </FormikProvider>
        </Dialog>
    );
}
